// DS7.4.1, but this only the root layout.
import React from 'react';
import c from 'classnames';
import isArray from 'lodash/isArray';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import s from './BannerLayout.scss';

interface Props {
  /**
   * Determined the height of the banner.
   */
  size: 'big' | 'small' | 'multiline';

  /**
   *
   * An array of nodes or an individual one. They'll be separated from each other and the main content by a small margin.
   * I recommend you pass a {@link BannerMessage} to this prop for a standard font-size and some typescript guidance.
   *
   * Note: This will be wrapped in a {@link Heading}.
   */
  body: React.ReactNode | React.ReactNode[];

  /**
   * An array of nodes or an individual one. They'll be separated from each other and the main content by a small margin.
   *
   * If you're passing a button, the recommended {@link ButtonSize} is `"small"`
   */
  postfix?: React.ReactNode | React.ReactNode[];
  className?: string;
  bodyClassName?: string;
}

/**
 * Takes an array of ReactNodes or an individual ReactNode and will wrap the latter in an array for
 * a more standard interface. This avoids nasty ternaries in the body of the below layout component.
 */
function nodesToArrayOrUndefined(
  nodes: React.ReactNode | React.ReactNode[] | undefined
) {
  if (nodes === undefined) {
    return nodes;
  }

  if (isArray(nodes)) {
    return nodes;
  }

  return [nodes];
}

/**
 * DS7.4.1, but only the layout. Use this in tandem with other {@link @wix/da-ds/pkg/Banner/lego} components when
 * building a variant of DS7.4.1
 */
const BannerLayout: React.FC<Props> = ({
  size,
  body,
  postfix,
  className,
  bodyClassName,
}) => {
  const bodyArray = nodesToArrayOrUndefined(body);
  const postfixArray = nodesToArrayOrUndefined(postfix);
  return (
    <aside className={c(s['root'], s[size], className)}>
      <Heading className={c(s['body-wrapper'], bodyClassName)}>
        {
          // A `bodyArray &&` here to appease typescript. Never should be undefined.
          bodyArray &&
            bodyArray.map((node, index) => (
              <div key={`body-${index}`} className={s['body-node']}>
                {node}
              </div>
            ))
        }
      </Heading>
      {postfixArray &&
        postfixArray.map((node, index) => (
          <div key={`postfix-${index}`} className={s['postfix']}>
            {node}
          </div>
        ))}
    </aside>
  );
};

BannerLayout.displayName = 'BannerLayout';

export default BannerLayout;
